import React, {useEffect, useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Heading,
    Button,
    Stack,
    Skeleton,
    Text,
    Flex,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalBody,
    IconButton,
    Icon,
    useStyleConfig,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import {AdyenCheckout, Card, Dropin} from '@adyen/adyen-web'
import {FormattedMessage, useIntl, defineMessages} from 'react-intl'
import {useHistory} from 'react-router-dom'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {getCreditCardIcon, buildMaskedCardDisplay} from '../../utils/cc-utils'

/* Hooks */
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useCustomToast} from '../../contexts/custom-toast-context'

/* Components */
/* Styling */
import adyen_css from './styles/adyen.css'

/* Icons */
import {LeftArrow, CustomClose} from '../../components/icons'

/* Constants */
const messages = defineMessages({
    cardError: {
        id: 'account_detail.payment.card_error',
        defaultMessage: 'There’s an error with the credit card. Please review and try again.'
    },
    errorRemovingCard: {
        id: 'account_detail.payment.error_removing_card',
        defaultMessage: 'There’s an error on removing the credit card.'
    },
    creditCardAdded: {
        id: 'account_detail.payment.credit_card_added',
        defaultMessage: 'Credit card added'
    },
    creditCardRemoved: {
        id: 'account_detail.payment.card_removed',
        defaultMessage: 'Credit card removed'
    },
    isDefault: {
        id: 'account_detail.payment.default',
        defaultMessage: 'Default'
    }
})

const customToastStyling = {
    position: {base: 'absolute', lg: 'relative'},
    top: {base: 0, lg: '-48px'},
    bottom: 'auto',
    width: '100%',
    minH: {base: '56px', lg: '48px'},
    paddingTop: {base: '19px', lg: '15px'},
    paddingBottom: {base: '19px', lg: '15px'},
    paddingLeft: {base: 4, lg: 3},
    paddingRight: {base: '22px', lg: '18px'},
    right: 0,
    left: 0,
    color: 'white',
    minW: {lg: '343px'},
    zIndex: 1410
}

const customToastMessageStyling = {
    fontSize: {base: '14px'},
    fontWeight: {base: 'normal'},
    lineHeight: {base: '18px'},
    letterSpacing: {base: '0px'},
}

/**
 * SavedPaymentMethod is a React functional component that displays a user's saved payment method details,
 * such as card type, last four digits, expiration date, and default status. It provides UI controls for
 * removing the payment method, including a confirmation modal dialog. The component supports responsive
 * design and uses custom hooks for toast notifications and internationalization. It also handles the
 * deletion of a payment instrument via a mutation and refreshes the payment methods list upon successful
 * removal. The component expects properties for payment details, customer identification, and a callback
 * to fetch updated payment methods.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} props.expirationMonth - The expiration month of the payment method.
 * @param {string} props.expirationYear - The expiration year of the payment method.
 * @param {string} props.id - The unique identifier for the payment method.
 * @param {string} props.type - The type of payment method (e.g., credit card type).
 * @param {string} props.lastFour - The last four digits of the payment method.
 * @param {boolean} props.isDefault - Indicates if this is the default payment method.
 * @param {string} props.customerId - The unique identifier for the customer.
 * @param {function} props.fetchPaymentMethods - Callback to refresh the list of payment methods.
 *
 * @returns {JSX.Element} The rendered payment method card with remove functionality.
 */
const SavedPaymentMethod = ({
    expirationMonth,
    expirationYear,
    id,
    type,
    lastFour,
    isDefault,
    customerId,
    fetchPaymentMethods,
    index
}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {showCustomToast} = useCustomToast()
    const intl = useIntl()
    const {isOpen, onOpen, onClose} = useDisclosure()
    const deleteCustomerPaymentInstrument = useShopperCustomersMutation(
        'deleteCustomerPaymentInstrument'
    )
    const CardTypeToIcon = getCreditCardIcon(type)
    const removeCard = async (id) => {
        try {
            await deleteCustomerPaymentInstrument.mutateAsync({
                parameters: {
                    customerId,
                    paymentInstrumentId: id
                }
            })

            onClose()
            fetchPaymentMethods()
            showCustomToast({
                message: intl.formatMessage(messages.creditCardRemoved),
                styles: {...customToastStyling, position: {base: 'relative', lg: 'static'}},
                messageStyles: {...customToastMessageStyling},
                containerId: isMobile ? 'app-header' : 'account-left-container'
            })
        } catch {
            showCustomToast({
                message: intl.formatMessage(messages.errorRemovingCard),
                styles: {...customToastStyling, position: {base: 'relative', lg: 'static'}},
                messageStyles: {...customToastMessageStyling},
                containerId: isMobile ? 'app-header' : 'account-left-container'
            })
            onClose()
        }
    }
    return (
        <Box>
            {isMobile && index === 0 &&
                <Text
                    fontSize={'xl'}
                    fontWeight={'medium'}
                    mt={2}
                    mb={10}>
                    <FormattedMessage
                        defaultMessage="Saved Credit Cards"
                        id="account_detail.payment.saved_cards"
                    />
                </Text>
            }
            <Box position={'relative'}>
                <Text
                    fontSize={'xs'}
                    textTransform={'uppercase'}
                    lineHeight={'15px'}
                    letterSpacing={'0px'}
                    color={'grey.600'}
                >
                    {isDefault && intl.formatMessage(messages.isDefault)}
                </Text>
                <Box
                    pt={isDefault && 4}
                    pb={4}
                    mb={4}
                    borderBottom={{base:'1px solid', lg: 0}}
                    borderColor={'grey.300'}
                >
                    <Flex justifyContent={'space-between'} alignItems={!isDefault && 'center'}>
                        <Flex alignItems={'center'} w="full">
                            <CardTypeToIcon width="100%" height="100%" maxW="42px" />{' '}
                            <Text as="span" pl={3} pr={8}>
                                {buildMaskedCardDisplay(lastFour, isMobile ? 12 : 4, isMobile ? '*' : '•')}
                            </Text>{' '}
                            {expirationMonth}/{expirationYear}
                            <IconButton
                                variant="unstyled"
                                ml="auto"
                                position={{base: isDefault && 'absolute', lg: 'static'}}
                                top={isDefault && '0'}
                                right={isDefault && '-2px'}
                                h={'auto'}
                                minW={'auto'}
                                onClick={onOpen}
                                icon={<Icon as={CustomClose} />}
                                aria-label="Close"
                            />
                        </Flex>
                    </Flex>
                </Box>
            </Box>

            <Modal
                onClose={onClose}
                isOpen={isOpen}
                isCentered={true}
                motionPreset={{base: 'slideInBottom', lg: 'none'}}
                size={'full'}
            >
                <ModalOverlay />
                <ModalContent
                    position={{base: 'fixed', lg: 'relative'}}
                    bottom={'0'}
                    left={'0'}
                    right={'0'}
                    top={'auto'}
                    transform={'none'}
                    minH={'auto'}
                    borderRadius={'0'}
                    maxWidth={{base: '100%', lg: '441px'}}
                >
                    <IconButton
                        aria-label="Close"
                        icon={<CustomClose />}
                        position="absolute"
                        w={{base: '24px'}}
                        h={{base: '24px'}}
                        top={{base: '12px', lg: '24px'}}
                        right={{base: '0', lg: '16px'}}
                        zIndex={'2'}
                        backgroundColor="transparent"
                        _hover={{backgroundColor: 'transparent'}}
                        color="black"
                        onClick={onClose}
                    />
                    <ModalBody padding={{base: '48px 16px 32px', lg: '79px 32px 24px'}}>
                        <Text
                            fontSize={{base: 'md', lg: 'sm'}}
                            lineHeight={{base: '24px', lg: '18px'}}
                        >
                            {isDefault ? (
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to delete your default payment?"
                                    id="account_detail.payment.delete_default"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Are you sure you want to delete your payment option?"
                                    id="account_detail.payment.delete_non_default"
                                />
                            )}
                        </Text>
                        <Button
                            onClick={() => removeCard(id)}
                            marginTop={{base: 6, lg: '40px'}}
                            width={{base: '100%'}}
                            minH={{base: '48px'}}
                        >
                            <FormattedMessage
                                defaultMessage="Remove Card"
                                id="account_detail.payment.remove_card"
                            />
                        </Button>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    )
}

SavedPaymentMethod.propTypes = {
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.string,
    id: PropTypes.string,
    type: PropTypes.string,
    lastFour: PropTypes.string,
    isDefault: PropTypes.bool,
    customerId: PropTypes.string,
    fetchPaymentMethods: PropTypes.func,
    index: PropTypes.number,
}

/**
 * HeadingPayment is a React functional component that renders a dynamic heading for the payment details section
 * of the user's account page. The heading content and navigation behavior are determined by the `headingMode` prop,
 * which can be one of 'nopayments', 'addcreditcard', or 'savedpayments'. The component adapts its layout and navigation
 * controls for mobile and desktop views using responsive design utilities.
 *
 * On mobile devices, a back button (with a left arrow icon) is conditionally displayed, allowing users to navigate back
 * to the account overview or to trigger a custom callback when adding a new credit card. The heading text is internationalized
 * using FormattedMessage for localization support.
 *
 * Props:
 *   @param {string} headingMode - Determines which heading and navigation controls to display. Supported values are
 *     'nopayments', 'addcreditcard', 'savedpayments', and 'unavailable'.
 *   @param {function} backFromAddCard - Callback function invoked when the back button is pressed in 'addcreditcard' mode.
 *
 * Usage:
 *   <HeadingPayment headingMode="addcreditcard" backFromAddCard={handleBack} />
 *
 * The component leverages Chakra UI for styling and layout, and integrates with React Router for navigation.
 */
const HeadingPayment = ({headingMode, backFromAddCard}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const history = useHistory()

    return (
        <Heading
            display={{base: 'flex'}}
            alignItems={{base: 'center'}}
            justifyContent={{base: 'center', lg: 'start'}}
            as="h2"
            fontSize={{base: '20px', lg: '24px'}}
            lineHeight={{base: '24px', lg: '30px'}}
            fontWeight={{base: 'bold'}}
            tabIndex="0"
            textTransform="uppercase"
            position={'relative'}
        >
            {/* No payment heading */}
            {headingMode === 'nopayments' && headingMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            position={'absolute'}
                            left={'0'}
                            m="0"
                            variant="link"
                            onClick={() => history.push('/account')}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    <FormattedMessage
                        defaultMessage="Saved Payment"
                        id="account_detail.title.saved_payment"
                    />
                </React.Fragment>
            )}

            {/* Add credit card heading */}
            {headingMode === 'addcreditcard' && headingMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            position={'absolute'}
                            left={'0'}
                            m="0"
                            variant="link"
                            onClick={backFromAddCard}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    <FormattedMessage
                        defaultMessage="New Credit Card"
                        id="account_detail.title.new_card"
                    />
                </React.Fragment>
            )}

            {/* Saved payments heading */}
            {headingMode === 'savedpayments' && headingMode !== 'unavailable' && (
                <React.Fragment>
                    {isMobile && (
                        <Button
                            position={'absolute'}
                            left={'0'}
                            m="0"
                            variant="link"
                            onClick={() => history.push('/account')}
                        >
                            <LeftArrow />
                        </Button>
                    )}
                    <FormattedMessage
                        defaultMessage="Saved Payment"
                        id="account_detail.title.payment_options"
                    />
                </React.Fragment>
            )}
        </Heading>
    )
}

HeadingPayment.propTypes = {
    headingMode: PropTypes.string,
    backFromAddCard: PropTypes.func
}

/**
 * AddPaymentMethod is a React functional component that provides a user interface for adding a new credit card payment method using Adyen's Drop-in integration.
 *
 * This component handles the entire payment method addition flow, including device fingerprinting, Adyen Drop-in initialization, form validation, error handling, and submission of payment details to the backend. It supports both desktop and mobile layouts, and provides feedback to the user in case of errors or validation issues.
 *
 * Key Features:
 * - Integrates with Adyen's Drop-in UI for secure credit card entry and 3DS authentication.
 * - Dynamically loads Adyen's device fingerprinting script and manages its lifecycle.
 * - Validates cardholder input fields and enables/disables the submission button accordingly.
 * - Handles payment submission, additional details (e.g., 3DS), and error states.
 * - Supports localization and multi-site configurations.
 * - Accepts payment methods data and a callback for when payment is completed.
 *
 * Props:
 * @param {Object} paymentMethods - The available payment methods response from Adyen, used to configure the Drop-in component.
 * @param {Function} onPaymentCompleted - Callback function invoked when the payment method has been successfully added.
 *
 * Usage:
 * <AddPaymentMethod paymentMethods={paymentMethods} onPaymentCompleted={handleCompleted} />
 *
 * Dependencies:
 * - AdyenCheckout, Dropin, Card (Adyen Web SDK)
 * - Chakra UI components and hooks
 * - React Intl for localization
 * - Custom hooks: useBreakpointValue, useStyleConfig, useAccessToken, useMultiSite
 * - PropTypes for prop validation
 */
const AddPaymentMethod = ({paymentMethods, onPaymentCompleted}) => {
    const isMobile = useBreakpointValue({base: true, lg: false})
    const styles = useStyleConfig('CustomAccountAdyenPayments')
    const intl = useIntl()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()
    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId
    const [fingerprintScriptLoaded, setFingerprintScriptLoaded] = useState(false)
    const [isDropinLoaded, setIsDropinLoaded] = useState(false)
    const [dropinConfigsReady, setDropinConfigsReady] = useState(false)
    const {locale} = useMultiSite()
    const paymentContainerRef = useRef(null)
    const dropinRef = useRef(null)
    const [errorMessage, setErrorMessage] = useState(null)
    const [isAddCardDisabled, setIsAddCardDisabled] = useState(true)
    const [notAdditionalDetailsView, setNotAdditionalDetailsView] = useState(true)
    const [fieldValidity, setFieldValidity] = useState({
        holderName: false,
        encryptedCardNumber: false,
        encryptedExpiryMonth: false,
        encryptedExpiryYear: false,
        encryptedSecurityCode: false
    })

    const onChange = (data, _component) => {
        const {valid} = data
        const cardType = data?.data?.paymentMethod?.brand

        // Update the security code label based on the card type
        if (_component?.dropinRef?.base?.querySelector('[data-id="encryptedSecurityCode"]')) {
            switch (cardType) {
                case 'amex':
                    _component.dropinRef.base.querySelector('[data-id="encryptedSecurityCode"]').textContent = '4 Digits'
                    break;
                default:
                    _component.dropinRef.base.querySelector('[data-id="encryptedSecurityCode"]').textContent = '3 Digits'
                    break;
            }
        }

        // Logic to disable the submit button based on field validation
        const relevantFields = {
            holderName: valid.holderName,
            encryptedCardNumber: valid.encryptedCardNumber,
            encryptedExpiryMonth: valid.encryptedExpiryMonth,
            encryptedExpiryYear: valid.encryptedExpiryYear,
            encryptedSecurityCode: valid.encryptedSecurityCode
        }

        setFieldValidity((prevState) => {
            const newState = {
                ...prevState,
                ...relevantFields
            }
            const allFieldsValid = Object.values(newState).every((isValid) => isValid)

            setIsAddCardDisabled(!allFieldsValid)

            return newState
        })
    }

    const checkoutConfiguration = {
        clientKey: appConfig.ADYEN_CLIENT_KEY,
        environment: appConfig.ADYEN_MODE,
        locale: locale.id,
        countryCode: locale.id.split('-')[1],
        cspNonce: Date.now().toString(),
        amount: {value: 0, currency: 'GBP'},
        paymentMethodsResponse: paymentMethods?.paymentMethods,
        translations: {
            [locale.id]: {
                /* Labels */
                'creditCard.holderName': 'Cardholder Name',
                'creditCard.cardNumber.label': 'Credit Card Number',
                'creditCard.expiryDate.label': 'MM/YY',
                'creditCard.securityCode.label': '',

                /* Errors */
                // Holder name errors
                'creditCard.holderName.invalid': 'Please fill out this field',

                // Credit card number errors
                'creditCard.numberField.invalid': 'Please enter the number as it appears on your card',
                'cc.num.900': 'Please enter the number as it appears on your card',
                'cc.num.901': 'Invalid Credit Card Number',

                // Expire date errors
                'cc.dat.912': 'This Credit Card has expired',
                'cc.dat.913': 'This Credit Card has expired',
                'cc.mth.915': 'Please select an Expiration Month',
                'cc.yr.917': 'Enter an expiration date',
                'cc.dat.910': 'Enter a valid expiry date',

                // Security code
                'cc.cvc.920': 'Please enter the security code',
                'cc.cvc.921': 'Invalid Security Code',
            }
        },
        onSubmit: async (state, _component, actions) => {
            try {
                const token = await getTokenWhenReady()
                const body = JSON.stringify({
                    orderId: 'recurringPayment-account',
                    adyenData: state,
                    deviceFingerprint: document.getElementById('adyen-device-fingerprint').value,
                    locale,
                    returnUrl: `${window.location.origin}/account/payment`
                })

                const paymentResult = await fetch(
                    `${proxyPath}/custom/adyen-payments/v1/organizations/${organizationId}/adyen-payments?siteId=${siteId}&c_brand=Garage`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: body
                    }
                )

                const result = await paymentResult.json()
                if (
                    !result.resultCode ||
                    result.resultCode === 'Error' ||
                    (result.additionalData &&
                        result.additionalData['tokenization.store.operationType'] ===
                            'alreadyExisting')
                ) {
                    actions.reject()
                    return
                }

                if (result?.action?.type === 'threeDS2') {
                    setNotAdditionalDetailsView(false)
                }

                actions.resolve(result)
            } catch (error) {
                console.error(
                    '[payment-details - AddPaymentMethod - checkoutConfiguration] onSubmit',
                    error
                )
                actions.reject()
            }
        },
        onAdditionalDetails: async (state, component, actions) => {
            try {
                let result = {}
                const token = await getTokenWhenReady()
                const additionalDetailsResult = await fetch(
                    `${proxyPath}/custom/adyen-payments-details/v1/organizations/${organizationId}/adyen-payments-details?siteId=${siteId}`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: `Bearer ${token}`
                        },
                        body: JSON.stringify(state.data)
                    }
                )

                result = await additionalDetailsResult.json()

                if (!result.adyenData && !result.adyenData.resultCode) {
                    actions.reject()
                    return
                }

                const {resultCode} = result.adyenData

                if (resultCode === 'Refused') {
                    setNotAdditionalDetailsView(true)
                }

                actions.resolve({
                    resultCode
                })
            } catch (error) {
                console.error('onSubmit', error)
                actions.reject()
            }
        },
        onPaymentCompleted: () => {
            onPaymentCompleted()
        },

        onPaymentFailed: async () => {
            setErrorMessage(intl.formatMessage(messages.cardError))
            refreshDropin()
        },

        onError: () => {
            setErrorMessage(intl.formatMessage(messages.cardError))
            refreshDropin()
        }
    }

    let [paymentMethodsConfiguration, setPaymentMethodsConfiguration] = useState({
        card: {
            enableStoreDetails: false,
            showStoredPaymentMethods: false,
            positionHolderNameOnTop: true,
            showPayButton: false,
            showBrandIcon: true,
            hasHolderName: true,
            holderNameRequired: true,
            name: 'Credit Card',
            amount: {value: 0, currency: 'GBP'},
            styles: {
                base: {
                    fontSize: '14px'
                },
                placeholder: {
                    fontWeight: '400',
                    lineHeight: '24px',
                    color: '#676767',
                    letterSpacing: '0px'
                }
            },
            placeholders: {
                cardNumber: 'Credit Card Number',
                expiryDate: 'MM/YY*',
                securityCodeThreeDigits: '3 Digits',
                securityCodeFourDigits: '4 Digits',
                holderName: 'Cardholder Name'
            },
            onLoad: () => {
                setIsDropinLoaded(true)
                setIsAddCardDisabled(!dropinRef?.current?.isValid)
            },
            onChange: onChange
        }
    })

    const refreshDropin = async () => {
        try {
            // Unmount existing dropin
            if (dropinRef.current) {
                dropinRef.current.unmount()
            }

            // Clear the container
            if (paymentContainerRef.current) {
                paymentContainerRef.current.innerHTML = ''
            }

            // Reset states
            setIsDropinLoaded(false)
            setIsAddCardDisabled(true)
            setFingerprintScriptLoaded(false)

            // Recreate dropin
            if (paymentMethods && paymentContainerRef?.current) {
                const checkout = await AdyenCheckout({...checkoutConfiguration, paymentMethods})
                const dropin = new Dropin(checkout, {
                    paymentMethodComponents: [Card],
                    paymentMethodsConfiguration: paymentMethodsConfiguration
                })?.mount(paymentContainerRef?.current)
                dropinRef.current = dropin
            }
        } catch (error) {
            console.error('Error refreshing dropin:', error)
        }
    }

    useEffect(() => {
        if (!isMobile) {
            setPaymentMethodsConfiguration((prevConfig) => {
                const newConfig = {...prevConfig}
                delete newConfig.card.placeholders
                return newConfig
            })
        }

        setDropinConfigsReady(true)
    }, [isMobile])

    useEffect(() => {
        try {
            const createCheckout = async () => {
                if (paymentMethods && paymentContainerRef?.current && dropinConfigsReady) {
                    const checkout = await AdyenCheckout({...checkoutConfiguration, paymentMethods})
                    const dropin = new Dropin(checkout, {
                        paymentMethodComponents: [Card],
                        paymentMethodsConfiguration: paymentMethodsConfiguration
                    })?.mount(paymentContainerRef?.current)
                    dropinRef.current = dropin
                }
            }
            createCheckout()
        } catch (error) {
            console.error('Error creating dropin:', error)
        }
    }, [paymentMethods, dropinConfigsReady])

    useEffect(() => {
        if (!fingerprintScriptLoaded && document.getElementById('adyen-device-fingerprint')) {
            document.getElementById('adyen-device-fingerprint').remove()
        }

        if (!document.getElementById('adyen-device-fingerprint')) {
            const input = document.createElement('input')
            input.type = 'hidden'
            input.id = 'adyen-device-fingerprint'
            input.name = 'adyen-device-fingerprint'
            document.body.appendChild(input)
        }

        if (!fingerprintScriptLoaded) {
            const script = document.createElement('script')
            const now = new Date()
            const yyyy = now.getFullYear()
            const mm = String(now.getMonth() + 1).padStart(2, '0')
            const dd = String(now.getDate()).padStart(2, '0')
            const version = `${yyyy}${mm}${dd}`
            script.src = `https://live.adyen.com/hpp/js/df.js?v=${version}`
            script.async = true
            script.nonce = window.__CSP_NONCE__
            script.onload = () => {
                setFingerprintScriptLoaded(true)
                window.dfSet('adyen-device-fingerprint')
            }
            document.body.appendChild(script)
        }
    }, [fingerprintScriptLoaded])

    const handleSubmitPayment = () => {
        const dropin = dropinRef?.current
        dropin.submit()
    }

    return (
        <Box
            key="adyen-card-container"
            id="card-container"
            css={[adyen_css]}
            sx={styles}
            maxW={{base: '343px', lg: '458px'}}
            margin={{base: '0 auto', lg: '0'}}
        >
            {errorMessage && !isMobile && (
                <Text
                    paddingBottom={{base: 6}}
                    fontSize={{base: 'xs'}}
                    color={{base: 'dangerText'}}
                >
                    {errorMessage}
                </Text>
            )}

            <div ref={paymentContainerRef} className={'payment floating-labels'}></div>

            {errorMessage && isMobile && (
                <Text fontSize={{base: 'xs'}} color={{base: 'dangerText'}}>
                    {errorMessage}
                </Text>
            )}

            {isDropinLoaded && notAdditionalDetailsView && (
                <Button
                    onClick={handleSubmitPayment}
                    marginTop={{base: 6}}
                    width={{base: '100%'}}
                    minH={{base: '48px'}}
                    isDisabled={isAddCardDisabled}
                >
                    <FormattedMessage
                        defaultMessage="Add Credit Card"
                        id="account_detail.title.add_credit_card"
                    />
                </Button>
            )}
        </Box>
    )
}

AddPaymentMethod.propTypes = {
    paymentMethods: PropTypes.object,
    onPaymentCompleted: PropTypes.func
}

/**
 * PaymentDetails is a React functional component that manages and displays the user's saved payment methods
 * within their account settings. It provides functionality to fetch, display, and add new credit cards,
 * leveraging Adyen's customer wallet API through a proxy. The component adapts its layout responsively
 * for mobile and desktop views, and uses various hooks for internationalization, authentication, and
 * toast notifications. It conditionally renders UI elements based on the presence of saved payment methods,
 * and provides user feedback upon successful addition of a new payment method. The component also handles
 * loading states and ensures the default payment method is prioritized in the display order.
 *
 * @component
 * @returns {JSX.Element} The rendered payment details section, including saved payment methods and the option to add new ones.
 */
const PaymentDetails = () => {
    const intl = useIntl()
    const isMobile = useBreakpointValue({base: true, lg: false})
    const {showCustomToast} = useCustomToast()
    const {data: customer} = useCurrentCustomer()
    const {getTokenWhenReady} = useAccessToken()
    const {app: appConfig} = getConfig()

    const parameters = {
        ...appConfig.commerceAPI.parameters
    }
    const proxyPath = appConfig.commerceAPI.proxyPath
    const organizationId = parameters.organizationId
    const siteId = parameters.siteId
    const [paymentMethods, setPaymentMethods] = useState(null)
    const [showAddPayment, setShowAddPayment] = useState(false)
    const [headingMode, setHeadingMode] = useState('nopayments')
    const [isPMSLoading, setIsPMSLoading] = useState(false)

    const fetchPaymentMethods = async () => {
        setIsPMSLoading(true)

        const token = await getTokenWhenReady()
        const response = await fetch(
            `${proxyPath}/custom/adyen-customer-wallet/v1/organizations/${organizationId}/customer-wallet?siteId=${siteId}`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                }
            }
        )
        const responseJson = await response.json()

        if (responseJson && responseJson.cards) {
            // Sort cards to have the default card first
            const sortedCards = responseJson.cards.sort((a, b) => b.default - a.default)
            setPaymentMethods({...responseJson, cards: sortedCards})
        }

        setIsPMSLoading(false)
    }

    useEffect(() => {
        fetchPaymentMethods()
    }, [])

    const handleAddCreditCard = () => {
        setShowAddPayment(true)
        setHeadingMode('addcreditcard')
    }

    const handleBackFromAddCard = () => {
        setShowAddPayment(false)
        setHeadingMode(paymentMethods?.cards?.length === 0 ? 'nopayments' : 'savedpayments')
    }

    const handleOnPaymentCompleted = async () => {
        setShowAddPayment(false)
        setHeadingMode(paymentMethods?.cards?.length === 0 ? 'nopayments' : 'savedpayments')
        showCustomToast({
            message: intl.formatMessage(messages.creditCardAdded),
            styles: {...customToastStyling, position: {base: 'relative', lg: 'static'}},
            messageStyles: {...customToastMessageStyling},
            containerId: isMobile ? 'app-header' : 'account-left-container'
        })
        await fetchPaymentMethods()
    }

    return (
        <Stack
            data-testid="account-payment"
            spacing={{base: 8, lg: '40px'}}
            height={{base: '100%'}}
            maxW={{base: '458px'}}
        >
            {isHydrated() && (
                <>
                    <HeadingPayment
                        key="account-payment-heading"
                        headingMode={headingMode}
                        backFromAddCard={handleBackFromAddCard}
                    />
                    <Box
                        data-testid="account-payment"
                        display={{base: 'flex'}}
                        justifyContent={{base: 'space-between'}}
                        flexDirection={{base: 'column'}}
                        flex={{base: '1'}}
                    >
                        <Skeleton
                            isLoaded={!isPMSLoading}
                            display={{base: 'flex'}}
                            justifyContent={{base: 'space-between'}}
                            flexDirection={{base: 'column'}}
                            flex={{base: '1'}}
                        >
                            {/* Add payment */}
                            {showAddPayment ? (
                                <AddPaymentMethod
                                    paymentMethods={paymentMethods}
                                    onPaymentCompleted={handleOnPaymentCompleted}
                                />
                            ) : (
                                <>
                                    {paymentMethods?.cards?.length === 0 ? (
                                        /* No saved payments */
                                        <Box textAlign={{base: 'left'}}>
                                            <Text
                                                fontSize={{base: 'md'}}
                                                lineHeight={{base: '20px'}}
                                                fontWeight={{base: 'bold'}}
                                                letterSpacing={{base: '0px'}}
                                                paddingBottom={{base: '14px', lg: 2}}
                                                textTransform={{base: 'uppercase'}}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="No Saved Payment Options"
                                                    id="account_detail.subtitle.no_saved_payment"
                                                />
                                            </Text>
                                            <Text
                                                fontSize={{base: 'md', lg: 'sm'}}
                                                lineHeight={{base: '24px', lg: '18px'}}
                                                paddingBottom={{lg: 2}}
                                                letterSpacing={{base: '0px'}}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Tap below to add payment option."
                                                    id="account_detail.subtitle.add_payment_option"
                                                />
                                            </Text>
                                        </Box>
                                    ) : (
                                        /* Saved payments */
                                        paymentMethods?.cards?.map((paymentMethod, index) => (
                                            <SavedPaymentMethod
                                                key={paymentMethod.id}
                                                {...paymentMethod}
                                                fetchPaymentMethods={fetchPaymentMethods}
                                                isDefault={paymentMethod.default}
                                                customerId={customer.customerId}
                                                index={index}
                                            />
                                        ))
                                    )}
                                    <Button
                                        onClick={handleAddCreditCard}
                                        fontSize={'md'}
                                        lineHeight={'20px'}
                                        marginLeft={{lg: 0}}
                                        marginRight={{lg: 0}}
                                        marginTop={{base: paymentMethods?.cards?.length > 0 ? 4 : 8}}
                                        maxW={{base: paymentMethods?.cards?.length > 0 ? '458px' : '343px'}}
                                        width={{base: '100%'}}
                                        minH={{base: '48px'}}
                                        h={{base: 'auto'}}
                                        p={'14px'}
                                    >
                                        <FormattedMessage
                                            defaultMessage="Add Credit Card"
                                            id="account_detail.title.add_credit_card"
                                        />
                                    </Button>
                                </>
                            )}
                        </Skeleton>
                    </Box>
                </>
            )}
        </Stack>
    )
}

PaymentDetails.getTemplateName = () => 'account-payment'

export default PaymentDetails
